/**
 *
 * Copyright © 2025-2026 Ping Identity Corporation. All right reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 *
 **/

import { journey } from '@forgerock/journey-client';
import { writable } from 'svelte/store';
import { z } from 'zod';

import { interpolate } from '$core/_utilities/i18n.utilities';
import { htmlDecode } from '$journey/_utilities/decode.utilities';
import { buildCallbackMetadata, buildStepMetadata } from '$journey/_utilities/metadata.utilities';
import {
  authIdTimeoutErrorCode,
  initCheckValidation,
  shouldPopulateWithPreviousCallbacks,
  shouldRedirectFromStep,
} from './stages/_utilities/step.utilities';

import type {
  BaseCallback,
  CustomLogger,
  GenericError,
  JourneyClient,
  JourneyClientConfig,
  JourneyResult,
  JourneyStep,
  LogLevel,
  NextOptions,
  RequestMiddleware,
  ResumeOptions,
  StartParam,
  Step,
} from '@forgerock/journey-client/types';
import type { Writable } from 'svelte/store';

import type { JourneyStore, JourneyStoreValue, StackStore, StepTypes } from './journey.interfaces';
import type { Maybe } from '$core/interfaces';

/**
 * Configure Journey Client
 */

const wellknownSchema = z
  .string({
    invalid_type_error:
      '`serverConfig.wellknown` must be a URL string (this is generated by the Zod library).',
    required_error:
      'Setting the `serverConfig.wellknown` is required (this is generated by the Zod library).',
  })
  .url({
    message: '`serverConfig.wellknown` must be a full URL (this is generated by the Zod library).',
  });

export const journeyClientConfigSchema: z.ZodType<JourneyClientConfig> = z
  .object({
    serverConfig: z
      .object({
        wellknown: wellknownSchema,
      })
      .strict(),
  })
  .strict();

// setJourneyClientConfig stores these; getJourneyClient() reads them when building the client.
// They never call each other, so module scope is the only handoff.
let journeyClientConfig: JourneyClientConfig | undefined;
let journeyRequestMiddleware: RequestMiddleware[] | undefined;
let journeyLogger: { level: LogLevel; custom?: CustomLogger } | undefined;

/**
 * We cache the journey client promise instead of only caching the resolved client so concurrent callers
 * share the same initialization work and we don't create multiple Journey Client instances in parallel.
 */
let journeyClientPromise: Promise<JourneyClient> | undefined;

/**
 * @function setJourneyClientConfig - Sets (or ensures) the Journey Client configuration.
 * Calling without a config is a no-op; the config requirement is enforced lazily
 * by `getJourneyClient()` at the point the client is actually needed.
 * @param {JourneyClientConfig} [config] If omitted, leaves existing config untouched.
 * @param {RequestMiddleware[]} [requestMiddleware] Optional request middleware forwarded to `journey()`.
 * @param {{ level: LogLevel; custom?: CustomLogger }} [logger] Optional logger (level + custom sink) forwarded to `journey()`.
 * @throws {z.ZodError} If provided config fails validation.
 * @returns {JourneyClientConfig | undefined} The active config, or undefined if none has been set.
 */
export function setJourneyClientConfig(
  config?: JourneyClientConfig,
  requestMiddleware?: RequestMiddleware[],
  logger?: { level: LogLevel; custom?: CustomLogger },
): JourneyClientConfig | undefined {
  if (config === undefined) {
    return journeyClientConfig;
  }

  /**
   * configure() calls setJourneyClientConfig(config, middleware, logger) — which stores logger into journeyLogger.
   * getJourneyClient() later reads journeyLogger and passes it to journey({ logger: journeyLogger }) when building the client.
   * setJourneyClientConfig stores it. getJourneyClient consumes it.
   */
  const parsed = journeyClientConfigSchema.parse(config);
  const hasChanged =
    parsed.serverConfig.wellknown !== journeyClientConfig?.serverConfig.wellknown ||
    requestMiddleware !== journeyRequestMiddleware ||
    logger !== journeyLogger;
  journeyClientConfig = parsed;
  journeyRequestMiddleware = requestMiddleware;
  journeyLogger = logger;
  // Reset the cached client promise when config changes.
  if (hasChanged) {
    journeyClientPromise = undefined;
  }
  return journeyClientConfig;
}

/**
 * @function getJourneyClient - Gets a cached Journey Client (promise).
 * @throws {Error} If no Journey Client configuration is available.
 * @returns {Promise<JourneyClient>} A promise that resolves to a Journey Client.
 */
export async function getJourneyClient(): Promise<JourneyClient> {
  if (!journeyClientConfig) {
    throw new Error('Journey Client is not configured. Call setJourneyClientConfig() first.');
  }

  // Build on first call; return the cached promise on every call after.
  if (!journeyClientPromise) {
    journeyClientPromise = journey({
      config: journeyClientConfig,
      requestMiddleware: journeyRequestMiddleware,
      ...(journeyLogger && { logger: journeyLogger }),
    }).catch((err) => {
      // If creation fails, clear the cache so a later call can try again.
      journeyClientPromise = undefined;
      throw err;
    });
  }

  return await journeyClientPromise;
}

/**
 * @function initializeJourney - Initializes the journey stack for tracking journey switches
 * @param {object} initOptions - The initial options to set
 * @returns {object} - The journey stack store with stack methods
 */
function initializeStack() {
  const { update, set, subscribe }: Writable<StartParam[]> = writable([]);

  // Assign to exported variable (see bottom of file)
  stack = {
    latest: async (): Promise<StartParam | undefined> => {
      return new Promise((resolve) => {
        // subscribe, grab the current value and unsubscribe
        subscribe((current) => {
          const lastItem = current[current.length - 1];
          resolve(lastItem);
        })();
      });
    },
    pop: async (): Promise<StartParam[]> => {
      return new Promise((resolve) => {
        update((current) => {
          let state;
          if (current.length) {
            state = current.slice(0, -1);
          } else {
            state = current;
          }
          resolve([...state]);
          return state;
        });
      });
    },
    push: async (options?: StartParam): Promise<StartParam[]> => {
      return new Promise((resolve) => {
        update((current) => {
          let state;

          if (!current.length) {
            state = options ? [options] : current;
          } else if (options && options?.journey !== current[current.length - 1]?.journey) {
            state = [...current, options];
          } else {
            state = current;
          }
          resolve([...state]);
          return state;
        });
      });
    },
    reset: () => {
      set([]);
    },
    subscribe,
  };

  return stack;
}

export const journeyStore: Writable<JourneyStoreValue> = writable({
  completed: false,
  error: null,
  loading: false,
  metadata: null,
  step: null,
  successful: false,
  response: null,
});

/**
 * @function initialize - Initializes the journey store
 * @param {JourneyClientConfig} [config] Optional Journey Client configuration.
 * @param {object} [initializationOptions] Optional metadata options threaded into callback metadata.
 * @param {RequestMiddleware[]} [requestMiddleware] Optional request middleware forwarded to `journey()`.
 * @param {{ level: LogLevel; custom?: CustomLogger }} [logger] Optional logger (level + custom sink) forwarded to `journey()`.
 * @throws {Error} If no Journey Client configuration is available.
 * @returns {JourneyStore} Journey store API.
 */
export function initialize(
  config?: JourneyClientConfig | undefined,
  initializationOptions?: Record<string, unknown> | null,
  requestMiddleware?: RequestMiddleware[],
  logger?: { level: LogLevel; custom?: CustomLogger },
): JourneyStore {
  setJourneyClientConfig(config, requestMiddleware, logger);

  const stack = initializeStack();
  let stepNumber = 0;
  let currentRecaptchaAction: string | null = null;

  async function start(startOptions?: StartParam, recaptchaAction?: string) {
    // Falls back to journey name (e.g. "Login") when no explicit recaptchaAction is given —
    // matches original behavior where the journey tree name was used as the Enterprise CAPTCHA action.
    currentRecaptchaAction = recaptchaAction ?? startOptions?.journey ?? null;
    journeyStore.update((current) => ({
      ...current,
      completed: false,
      error: null,
      loading: true,
      step: null,
      successful: false,
      response: null,
    }));

    if (startOptions) {
      await stack.push(startOptions);
    }

    let result;
    try {
      const journeyClient = await getJourneyClient();
      result = await journeyClient.start(startOptions);
    } catch (err) {
      console.error(`Start request | ${err}`);
      result = toGenericError(err);
    }
    await handleJourneyResult(result);
  }

  async function next(prevStep: JourneyStep, nextOptions?: NextOptions) {
    const previousCallbacks = prevStep.callbacks;
    const previousPayload = prevStep.payload;

    journeyStore.update((current) => ({
      ...current,
      completed: false,
      error: null,
      loading: true,
      step: prevStep,
      successful: false,
      response: null,
    }));

    let result;
    try {
      const journeyClient = await getJourneyClient();
      result = await journeyClient.next(prevStep, nextOptions);
    } catch (err) {
      console.error(`Next step request | ${err}`);
      result = toGenericError(err);
    }
    await handleJourneyResult(result, {
      prevStep,
      previousCallbacks,
      previousPayload,
      nextOptions,
    });
  }

  async function resume(url: string, resumeOptions?: ResumeOptions) {
    journeyStore.update((current) => ({
      ...current,
      completed: false,
      error: null,
      loading: true,
      step: current.step ?? null,
      successful: false,
      response: null,
    }));

    let result;
    try {
      const journeyClient = await getJourneyClient();
      /**
       * Journey Client `resume()` parses the legacy URL params itself (error, errorCode,
       * errorMessage, nonce, RelayState, scope, suspendedId, authIndexValue, plus the
       * redirect params code/state/form_post_entry/responsekey). The one thing it does not
       * read is a `journey` query param, so forward that through when present.
       */
      const journeyParam = new URL(url).searchParams.get('journey');
      const updatedResumeOptions = journeyParam
        ? { ...resumeOptions, journey: journeyParam }
        : resumeOptions;

      result = await journeyClient.resume(url, updatedResumeOptions);
    } catch (err) {
      console.error(`Resume request | ${err}`);
      result = toGenericError(err);
    }
    await handleJourneyResult(result);
  }

  async function push(newOptions: StartParam) {
    reset();
    await stack.push(newOptions);
    await start(newOptions);
  }

  async function pop() {
    reset();
    const updatedStack = await stack.pop();
    const currentJourney = updatedStack[updatedStack.length - 1];
    await start(currentJourney);
  }

  async function redirect(step: JourneyStep) {
    if (!shouldRedirectFromStep(step)) {
      return;
    }

    try {
      const journeyClient = await getJourneyClient();
      await journeyClient.redirect(step);
    } catch (err) {
      console.error(`Redirect request | ${err}`);
    }
  }

  function reset() {
    journeyStore.set({
      completed: false,
      error: null,
      loading: false,
      metadata: null,
      step: null,
      successful: false,
      response: null,
    });
  }

  async function handleJourneyResult(
    result: JourneyResult,
    context?: {
      prevStep?: StepTypes;
      previousCallbacks?: BaseCallback[];
      previousPayload?: Step;
      nextOptions?: NextOptions;
    },
  ) {
    if (result.type === 'Step') {
      const stepResult = result as JourneyStep;
      const stageAttribute = stepResult.getStage();

      let stageJson: Maybe<Record<string, unknown>> = null;
      let stageName: Maybe<string> = null;

      // Check if stage attribute is serialized JSON
      if (stageAttribute && stageAttribute.includes('{')) {
        try {
          stageJson = JSON.parse(stageAttribute);
        } catch {
          console.warn('Stage attribute value was not parsable');
        }
      } else if (stageAttribute) {
        stageName = stageAttribute;
      }

      const callbackMetadata = buildCallbackMetadata(stepResult, initCheckValidation(), stageJson, {
        ...initializationOptions,
        recaptchaAction: currentRecaptchaAction,
      });
      const stepMetadata = buildStepMetadata(callbackMetadata, stageJson, stageName);

      // Iterate on a successful progression
      stepNumber = stepNumber + 1;
      journeyStore.update((current) => ({
        ...current,
        completed: false,
        error: null,
        loading: false,
        metadata: {
          callbacks: callbackMetadata,
          step: stepMetadata,
        },
        step: stepResult,
        successful: false,
        response: null,
      }));
    } else if (result.type === 'LoginSuccess') {
      /**
       * SUCCESSFUL COMPLETION BLOCK
       */
      stack.reset();

      // Set final state
      journeyStore.update((current) => ({
        ...current,
        completed: true,
        error: null,
        loading: false,
        metadata: null,
        step: null,
        successful: true,
        response: result.payload,
      }));
    } else if (result.type === 'LoginFailure') {
      const failureResult = result;
      const failureMessageStr = htmlDecode(failureResult.payload?.message || 'Unknown login error');

      await restartJourney(failureMessageStr, context, failureResult);
    } else {
      // Handle GenericError case
      const genericError = result;
      const errorMessage =
        genericError.message ?? genericError.error ?? interpolate('unknownNetworkError');

      await restartJourney(errorMessage, context);
    }
  }

  async function restartJourney(
    errorMessage: Maybe<string>,
    context?: {
      prevStep?: StepTypes;
      previousCallbacks?: BaseCallback[];
      previousPayload?: Step;
      nextOptions?: NextOptions;
    },
    failureResult?: Extract<JourneyResult, { type: 'LoginFailure' }>,
  ) {
    let restartedResult: JourneyResult | null = null;

    try {
      /**
       * Restart journey to get fresh step
       */
      const restartOptions = await stack.latest();
      const journeyClient = await getJourneyClient();
      restartedResult = await journeyClient.start(restartOptions);

      /**
       * Now that we have a new authId (the identification of the
       * fresh step) let's populate this new step with old callback data if
       * this is step one and meets a few criteria.
       *
       * If error code is 110 or error message includes "Constrained Violation",
       * then the issue needs special handling.
       *
       * If this is the first step in the journey, replace the callbacks with
       * existing callbacks to resubmit with a fresh authId.
       ******************************************************************* */
      if (
        failureResult &&
        restartedResult.type === 'Step' &&
        shouldPopulateWithPreviousCallbacks(
          failureResult,
          context?.previousCallbacks,
          restartedResult,
          stepNumber,
        )
      ) {
        const restartedStep = restartedResult;
        restartedStep.callbacks = context?.previousCallbacks as BaseCallback[];

        // Rebuild payload onto restartedStep ensuring the use of the NEW authId
        restartedStep.payload = {
          ...(context?.previousPayload as Step),
          authId: restartedStep.payload.authId,
        };

        const details = failureResult.payload.detail as { errorCode: string } | null;

        /**
         * Only if the authId expires do we resubmit with same callback values
         */
        if (details?.errorCode === authIdTimeoutErrorCode) {
          restartedResult = await journeyClient.next(restartedStep, context?.nextOptions);
        }
      }
    } catch (err) {
      console.error(`Restart failed step request | ${err}`);
      restartedResult = toGenericError(err);
    }

    /**
     * SET RESULT OF SUBSEQUENT REQUEST
     *
     * After the above attempts to salvage the form submission, let's return
     * the final result to the user.
     */
    if (restartedResult.type === 'Step') {
      const restartedStep = restartedResult;
      const stageAttribute = restartedStep.getStage();

      let stageJson: Maybe<Record<string, unknown>> = null;
      let stageName: Maybe<string> = null;

      if (stageAttribute && stageAttribute.includes('{')) {
        try {
          stageJson = JSON.parse(stageAttribute);
        } catch {
          console.warn('Stage attribute value was not parsable');
        }
      } else if (stageAttribute) {
        stageName = stageAttribute;
      }

      const callbackMetadata = buildCallbackMetadata(
        restartedStep,
        initCheckValidation(),
        stageJson,
        { ...initializationOptions, recaptchaAction: currentRecaptchaAction },
      );
      const stepMetadata = buildStepMetadata(callbackMetadata, stageJson, stageName);

      journeyStore.update((current) => ({
        ...current,
        completed: false,
        error: {
          code: failureResult?.getCode() ?? null,
          message: errorMessage,
          stage: context?.prevStep?.payload?.stage ?? null,
          troubleshoot: null,
          detail: failureResult?.payload?.detail ?? null,
        },
        loading: false,
        metadata: {
          callbacks: callbackMetadata,
          step: stepMetadata,
        },
        step: restartedStep,
        successful: false,
        response: null,
      }));
      return;
    } else if (restartedResult.type === 'LoginSuccess') {
      journeyStore.update((current) => ({
        ...current,
        completed: true,
        error: null,
        loading: false,
        metadata: null,
        step: null,
        successful: true,
        response: restartedResult.payload,
      }));
      return;
    } else {
      journeyStore.update((current) => ({
        ...current,
        completed: true,
        error: {
          code: failureResult?.getCode() ?? null,
          message: errorMessage,
          stage: context?.prevStep?.payload?.stage ?? null,
          troubleshoot: null,
          detail: failureResult?.payload?.detail ?? null,
        },
        loading: false,
        metadata: null,
        step: null,
        successful: false,
        response: restartedResult.type === 'LoginFailure' ? restartedResult.payload : null,
      }));
    }
  }

  function toGenericError(err: unknown): GenericError {
    const message = err instanceof Error ? err.message : interpolate('unknownNetworkError');
    return {
      error: 'unknown_error',
      message,
      type: 'unknown_error',
    };
  }

  return {
    next,
    pop,
    push,
    reset,
    resume,
    start,
    redirect,
    subscribe: journeyStore.subscribe,
  };
}

export let stack: StackStore;
